import React, { MouseEvent, useEffect } from 'react';
import { Stack, PrimaryButton } from '@fluentui/react';
import { FormEvent } from 'react';
import { appStyles } from '../../helpers';
import { useAppSelector, useAppDispatch } from '../../store';
import { renameFileThunk } from '../../store/actions/app/thunks';
import { selectTranslate } from '../../store/selectors/ui';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { ControlledTextField } from './controls/controlledTextField';
import * as path from 'path';
import { editFilenameFormDataSchema } from '../../schemas/editFilename';
import { RenameFileData } from '../../model';

type Props = {
    initialPath: string;
    onCancel: (event: MouseEvent<HTMLButtonElement> | null) => void;
};

export const EditFilenameForm = ({ initialPath, onCancel }: Props) => {
    const translate = useAppSelector(selectTranslate);
    const {
        handleSubmit,
        register,
        control,
        formState: { errors },
        setError,
    } = useForm<RenameFileData>({
        defaultValues: async () => {
            return {
                initialPath,
                newFilename: path.parse(initialPath).name,
            };
        },
        resolver: zodResolver(editFilenameFormDataSchema),
        mode: 'onSubmit',
    });

    const dispatch = useAppDispatch();
    const onSubmitHandler = (
        data: RenameFileData,
        _e: FormEvent<HTMLFormElement>,
    ) => {
        onCancel(null);
        dispatch(renameFileThunk(data));
    };

    useEffect(() => {
        const err = errors[''];
        if (!err) {
            return;
        }
        setError('newFilename', err);
    }, [errors['']]);

    return (
        <form onSubmit={handleSubmit(onSubmitHandler)}>
            <Stack tokens={{ childrenGap: '1rem' }}>
                <ControlledTextField
                    control={control}
                    {...register('newFilename')}
                    label={translate('enter_name')}
                />
                <Stack
                    horizontal={true}
                    horizontalAlign="end"
                    tokens={{ childrenGap: '1rem' }}
                >
                    <PrimaryButton
                        type="submit"
                        styles={appStyles.primaryButton}
                    >
                        {translate('save')}
                    </PrimaryButton>
                    <PrimaryButton
                        styles={appStyles.primaryButton}
                        onClick={onCancel}
                    >
                        {translate('button_cancel')}
                    </PrimaryButton>
                </Stack>
            </Stack>
        </form>
    );
};
